import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Column, usePagination, useSortBy, useTable } from 'react-table';
import { TimeRangeContext } from '../../../context/TimeRangeContext';
import api from '../../../api/Base';
import Helper from '../../../helpers/Helper';
import { getDefaultPageSize, getToolTipPosition } from '../../../helpers/utils';
import Alert from '../../../component/Alert';
import ReactTable from '../../../component/ReactTable';
import StatementToolTip from '../../../component/instance/table/StatementToolTip';
import StatementBreakdown from '../../../component/instance/StatementBreakdown';
import TableCSVExport from '../../../component/TableCSVExport';
import SearchInput from '../../../component/SearchInput';
import { CHART_COLOURS_STATEMENTS, CHART_COLOURS_WAITS } from '../../../component/Constants';
import { INSTANCE_TYPE_ORACLE, INSTANCE_TYPE_POSTGRES, INSTANCE_TYPE_SQLSERVER } from '../../../component/Constants';
import InstanceTarget from '../../../types/instance/InstanceTarget';
import Wait from '../../../types/instance/Wait';
import Statement from '../../../types/instance/Statement';
import StatementsDataTable from '../../../types/instance/tables/StatementsDataTable';

type TagGroupStatementRow = StatementsDataTable & { time?: number; weight?: number };

interface TagGroupStatementsTableProps {
    tagName: string;
    tagValue: string;
    instances: InstanceTarget[];
    instanceTime: number;
}

const TagGroupStatementsTable: React.FC<TagGroupStatementsTableProps> = ({
    tagName,
    tagValue,
    instances,
    instanceTime
}) => {
    const { getTimeRangeQueryString, getTimeRangeRedirectString } = useContext(TimeRangeContext);
    const [statementsDataTable, setStatementsDataTable] = useState<TagGroupStatementRow[]>([]);
    const [waits, setWaits] = useState<Wait[]>([]);
    const [loading, setLoading] = useState(true);
    const [tableFilter, setTableFilter] = useState('');
    const [visibleToolTip, setVisibleToolTip] = useState({ id: '', position: 0 });

    const firstInstance = instances[0];
    const tagFilter = `${tagName}:${tagValue}`;

    useEffect(() => {
        if (!tagName || !tagValue || instances.length === 0) {
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                const tagParam = `tag=${encodeURIComponent(tagFilter)}`;
                const [statementsRes, waitsRes] = await Promise.all([
                    api.get(`activity/sql?limit=${process.env.REACT_APP_API_LIMIT}&${getTimeRangeQueryString()}&statistic=executions&sort=waittime+desc&${tagParam}`),
                    api.get(`activity/waitevent?limit=${process.env.REACT_APP_API_LIMIT}&${getTimeRangeQueryString()}&sort=waittime+desc&${tagParam}`)
                ]);

                const statementsData = (statementsRes.data || []).map((item: any, index: number) => {
                    const count = item.waitcount ?? item.executions;
                    const executions = count !== undefined && count !== null ? Number(count) : null;
                    const waittime = item.waittime ?? 0;
                    const averagetime = item.avgwaittime ?? (executions && executions > 0 ? waittime / executions : null);
                    const statement: Statement = {
                        database: item.database || '',
                        color: CHART_COLOURS_STATEMENTS[index % CHART_COLOURS_STATEMENTS.length],
                        sqlhash: item.sqlhash,
                        sqlid: item.sqlid || '',
                        sqltext: item.sqltext || '',
                        username: item.username || '',
                        batchexecutions: item.batchexecutions ?? 0,
                        executions: executions ?? 0,
                        waittime,
                        batchsqlhash: item.batchsqlhash || ''
                    };
                    return {
                        id: index,
                        color: statement.color,
                        sqlhash: item.sqlhash,
                        sqltext: (item.sqltext || '').slice(0, 1000),
                        executions,
                        waittime,
                        averagetime: averagetime != null ? Number(averagetime.toFixed(2)) : null,
                        statement,
                        time: waittime
                    };
                });

                const waitsData = (waitsRes.data || []).map((item: any, index: number) => ({
                    ...item,
                    color: CHART_COLOURS_WAITS[index % CHART_COLOURS_WAITS.length]
                }));

                setStatementsDataTable(statementsData);
                setWaits(waitsData);
            } catch (error) {
                console.error('Error fetching tag group data:', error);
                setStatementsDataTable([]);
                setWaits([]);
            } finally {
                setLoading(false);
            }
        };

        void fetchData();
    }, [tagName, tagValue, tagFilter, instances.length, getTimeRangeQueryString]);

    const data = useMemo(() =>
        statementsDataTable.filter(row =>
            (row.sqlhash || '').toLowerCase().includes(tableFilter.toLowerCase()) ||
            (row.sqltext || '').toLowerCase().includes(tableFilter.toLowerCase()) ||
            (row.statement?.sqlid || '').toLowerCase().includes(tableFilter.toLowerCase())
        ),
        [statementsDataTable, tableFilter]
    );

    const hiddenColumns = useMemo(() => {
        if (!firstInstance) return ['statement', 'id', 'color'];
        switch (firstInstance.type) {
            case INSTANCE_TYPE_SQLSERVER:
            case INSTANCE_TYPE_ORACLE:
                return ['statement', 'id', 'color', 'executions', 'averagetime'];
            case INSTANCE_TYPE_POSTGRES:
                return firstInstance.use_pg_stat_statements ? ['statement', 'id', 'color'] : ['statement', 'id', 'color', 'executions', 'averagetime'];
            default:
                return ['statement', 'id', 'color', 'executions', 'averagetime'];
        }
    }, [firstInstance]);

    const columns: Column<TagGroupStatementRow>[] = useMemo(() => [
        { accessor: 'statement', disableSortBy: true },
        { accessor: 'color', disableSortBy: true },
        { accessor: 'id', disableSortBy: true },
        {
            Header: '#',
            accessor: 'sqlhash',
            className: 'concatenate-small col-width-10',
            defaultCanSort: true,
            sortType: (rowA: any, rowB: any, columnId: string) => {
                const a = Number(rowA.values[columnId]);
                const b = Number(rowB.values[columnId]);
                return a - b;
            },
            Cell: ({ cell }: any) => (
                <React.Fragment>
                    <span className="me-1 ps-1" style={{ backgroundColor: cell.row.values.color }}>&nbsp;</span>
                    {firstInstance && (
                        <Link to={`/instances/${firstInstance.id}/activity/statement/${cell.row.values.sqlhash}/text?${getTimeRangeRedirectString()}`}>
                            <i className="fal fa-arrow-up-right-from-square fa-fw" data-tooltip-id="global-tooltip" data-tooltip-content="Open in new page" /> {cell.row.values.sqlhash}
                        </Link>
                    )}
                </React.Fragment>
            )
        },
        {
            Header: 'Statement',
            accessor: 'sqltext',
            className: 'concatenate',
            disableSortBy: true,
            Cell: ({ cell }: any) => {
                if (!cell.row.values.sqltext) return <React.Fragment>-</React.Fragment>;
                const link = firstInstance
                    ? `/instances/${firstInstance.id}/activity/statement/${cell.row.values.sqlhash}/text?${getTimeRangeRedirectString()}`
                    : '#';
                return <Link to={link}>{cell.row.values.sqltext.substring(0, 200)}</Link>;
            }
        },
        {
            Header: 'Breakdown',
            className: 'statement-waits col-width-10',
            disableSortBy: true,
            Cell: ({ cell }: any) => (
                cell.row.values.waittime != null ? (
                    cell.row.values.id < 10 ? (
                        <StatementBreakdown
                            key={cell.row.values.sqlhash}
                            filterParameters=""
                            statement={cell.row.values.statement}
                            waits={waits}
                            instanceId={firstInstance?.id ?? 0}
                            instanceTime={instanceTime}
                            maxWaitTime={data[0]?.waittime}
                            tagFilter={tagFilter}
                        />
                    ) : (
                        cell.row.values.id < 20 ? (
                            <StatementBreakdown
                                key={cell.row.values.sqlhash}
                                filterParameters=""
                                statement={cell.row.values.statement}
                                waits={waits}
                                instanceId={firstInstance?.id ?? 0}
                                instanceTime={instanceTime}
                                maxWaitTime={data[0]?.waittime}
                                tagFilter={tagFilter}
                            />
                        ) : (
                            <React.Fragment>-</React.Fragment>
                        )
                    )
                ) : (
                    <React.Fragment>-</React.Fragment>
                )
            )
        },
        {
            Header: 'Executions',
            accessor: 'executions',
            headerClassName: 'text-end',
            className: 'text-end col-width-5',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }: any) => (
                cell.row.values.executions != null && cell.row.values.executions !== 0
                    ? Helper.getFormattedNumber(cell.row.values.executions)
                    : <React.Fragment>-</React.Fragment>
            )
        },
        {
            Header: 'Average Time',
            accessor: 'averagetime',
            headerClassName: 'text-end',
            className: 'text-end col-width-5',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }: any) => (
                cell.row.values.averagetime != null && cell.row.values.executions != null && cell.row.values.executions > 0
                    ? Helper.getTimeInEnglish(cell.row.values.averagetime)
                    : <React.Fragment>-</React.Fragment>
            )
        },
        {
            Header: 'Total Time',
            accessor: 'waittime',
            headerClassName: 'text-end',
            className: 'text-end col-width-5 text-nowrap',
            defaultCanSort: true,
            sortType: (rowA: any, rowB: any, columnId: string) => {
                const aWait = Number(rowA.values[columnId]) || 0;
                const bWait = Number(rowB.values[columnId]) || 0;
                if (aWait !== bWait) return bWait - aWait;
                const aHash = Number(rowA.values['sqlhash']) || 0;
                const bHash = Number(rowB.values['sqlhash']) || 0;
                return aHash - bHash;
            },
            Cell: ({ cell }: any) => (
                cell.row.values.waittime != null
                    ? Helper.getTimeInEnglish(cell.row.values.waittime)
                    : <React.Fragment>-</React.Fragment>
            )
        },
        {
            Header: 'Weight %',
            accessor: 'weight',
            headerClassName: 'text-end',
            className: 'text-end col-width-5',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }: any) => {
                const { waittime } = cell.row.values;
                const percentage = instanceTime ? (100 / instanceTime) * waittime : 0;
                return <React.Fragment>{instanceTime ? `${Helper.formatPercentage(percentage)}%` : '-'}</React.Fragment>;
            }
        }
    ], [firstInstance, getTimeRangeRedirectString, instanceTime, waits, tagFilter, data]);

    const tableInstance = useTable(
        {
            columns,
            data,
            initialState: {
                hiddenColumns,
                pageIndex: 0,
                pageSize: getDefaultPageSize()
            }
        },
        useSortBy,
        usePagination
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = tableInstance;

    function clearTableFilter() {
        setTableFilter('');
    }

    return (
        <div className="card mb-4">
            <div className="card-header">
                <i className="fal fa-scroll fa-fw" aria-hidden="true" />
                Statements (tag: {tagName}={tagValue})
                <i
                    className="collapse-toggle"
                    role="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseTagGroupStatements"
                    aria-expanded="false"
                    aria-controls="collapseTagGroupStatements"
                />
            </div>
            <div id="collapseTagGroupStatements" className="card-body collapse show">
                {Number(process.env.REACT_APP_API_LIMIT) - 1 === data.length - 1 && (
                    <Alert
                        message={`To help preserve performance, we limit the total number of statement records below to the top ${Number(process.env.REACT_APP_API_LIMIT) - 1} in descending order by their total wait time.`}
                        heading="Statement records"
                        variant="alert-info"
                    />
                )}
                <div className="row row-cols-1 row-cols-md-2 table-search">
                    <div className="col col-md-10">
                        <TableCSVExport
                            tableInstance={tableInstance}
                            filename="Statements"
                            instanceTime={instanceTime}
                        />
                    </div>
                    <div className="col col-md-2">
                        <SearchInput
                            value={tableFilter}
                            onChange={(e) => setTableFilter(e.target.value)}
                            onClear={clearTableFilter}
                        />
                    </div>
                </div>
                <ReactTable
                    getTableProps={getTableProps}
                    getTableBodyProps={getTableBodyProps}
                    headerGroups={headerGroups}
                    prepareRow={prepareRow}
                    page={page}
                    canPreviousPage={canPreviousPage}
                    canNextPage={canNextPage}
                    pageOptions={pageOptions}
                    pageCount={pageCount}
                    gotoPage={gotoPage}
                    nextPage={nextPage}
                    previousPage={previousPage}
                    setPageSize={setPageSize}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    dataLength={data.length}
                    error={null}
                    loading={loading === true}
                    renderCell={(cell) => {
                        if (cell.column.id === 'sqltext') {
                            return (
                                <div
                                    className="tooltip-scroll ellipsis"
                                    onMouseEnter={(event) =>
                                        setVisibleToolTip({
                                            id: cell.row.values.sqlhash,
                                            position: getToolTipPosition(event)
                                        })
                                    }
                                    onMouseLeave={() => setVisibleToolTip({ id: '', position: 0 })}
                                >
                                    {cell.render('Cell')}
                                    {firstInstance && cell.row.values.sqlhash === visibleToolTip.id && (
                                        <StatementToolTip
                                            instanceType={firstInstance.type}
                                            position={visibleToolTip.position}
                                            id={cell.row.values.sqlhash}
                                            link={`/instances/${firstInstance.id}/activity/statement/${cell.row.values.sqlhash}/text?${getTimeRangeRedirectString()}`}
                                            cell={cell}
                                        />
                                    )}
                                </div>
                            );
                        }
                        return cell.render('Cell');
                    }}
                />
            </div>
        </div>
    );
};

export default TagGroupStatementsTable;
